import {
  Button,
  Checkbox,
  Flex,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { modals } from '@mantine/modals'
import withErrorBoundaryAndPolicyCheck from '@v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'
import showModalNotification from '@v2/commons/modals/ModalNotification/showModalNotification'
import useGetUsersNoPaginationUseCase from '@v2/domains/user/hooks/useGetUsersNoPagination.useCase'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'
import { useEffect, useState } from 'react'

import { Icons } from 'src/common/components/ui'
import { useRouter } from 'src/v2/commons/utils/navigation'

import ModalSelect from '../../../../commons/components/ModalSelect/modalSelect.component'
import TaskNotes from '../../../task/components/TaskEscalationNotes/taskEscalationNotes.component'
import useUpdateTask from '../../hooks/updateTask.hook'
import {
  ITask,
  TASK_ACTION_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  TASK_SUBJECT_OPTIONS,
  TASK_TYPE_OPTIONS,
  TaskActionEnum,
  TaskTypeEnum,
} from '../../task.types'
import { transformUpdateTaskFormValues } from '../../utils/valuesTransformer.util'
import { TOOLTIP_SUBJECT } from './assignTaskModal.component'
import styles from './task-component.module.scss'

export interface ModalProps {
  selectedItem: Partial<ITask> | null
  resetPaginate: () => void
}

const UpdateTaskModal = ({ selectedItem, resetPaginate }: ModalProps) => {
  const router = useRouter()
  const {
    updateTaskForm,
    onSubmitForm,
    updateTaskMutationStatus,
    hasUpdateTaskPolicy,
  } = useUpdateTask()

  const { subject: defaultSubject, action: defaultAction } =
    selectedItem as ITask

  const { assignees, usersQueryStatus } = useGetUsersNoPaginationUseCase()
  const [taskSubjectValues, setTaskSubjectValues] = useState<
    typeof TASK_SUBJECT_OPTIONS
  >([])

  const [subject, setSubject] = useState<string | null | undefined>(
    () => defaultSubject,
  )
  const [action, setAction] = useState<string | null | undefined>(
    () => defaultAction,
  )
  const [assignPerson, setAssignPerson] = useState<boolean | null | undefined>(
    updateTaskForm.getValues()['type'] === TaskTypeEnum.FOLLOW_UP,
  )

  updateTaskForm.watch('action', ({ value }) => {
    onSetSubjectValuesByAction(value!)
    setAction(value)
  })
  updateTaskForm.watch('subject', ({ value }) => {
    setSubject(value)
  })

  updateTaskForm.watch('type', ({ value }) => {
    if (value === TaskTypeEnum.FOLLOW_UP) {
      setAssignPerson(true)
    } else {
      setAssignPerson(false)
      updateTaskForm.setFieldValue('isPersonAssign', false)
    }
  })

  const onSetSubjectValuesByAction = (value: TaskActionEnum) => {
    const subjectOptions = TASK_SUBJECT_OPTIONS.filter((option) =>
      option.action.includes(value!),
    )
    const subject = defaultAction === value ? defaultSubject : ''
    updateTaskForm.setFieldValue('subject', subject)
    setSubject(subject)
    setTaskSubjectValues(subjectOptions)
  }

  useEffect(() => {
    if (selectedItem) {
      const transformedValues = transformUpdateTaskFormValues(selectedItem)
      updateTaskForm.setValues(transformedValues)
    }
    return () => updateTaskForm.reset()
  }, [selectedItem])

  useEffect(() => {
    if (updateTaskMutationStatus.isSuccess) {
      modals.closeAll()
      showModalNotification({
        title: 'Success',
        subtitle: 'Task updated successfully',
      })
      resetPaginate()
    }
  }, [updateTaskMutationStatus.isSuccess])

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmitForm(transformUpdateTaskFormValues(selectedItem as Partial<ITask>))
  }

  if (usersQueryStatus.isLoading) {
    return (
      <Group justify="center" mb="lg">
        <Loader size="md" />
      </Group>
    )
  }
  return (
    <div className={styles['modal-body']}>
      <Text className={styles['modal-header']} ta="center">
        Update Task
      </Text>
      <form
        onSubmit={(e) => {
          handleSubmit(e)
        }}
      >
        <SimpleGrid
          cols={{ base: 1, sm: 2 }}
          spacing="xl"
          className={styles['simpleGrid-root']}
        >
          <TextInput
            readOnly
            label="Record Name"
            classNames={{
              input: styles['record-info-input'],
              label: styles['record-info-label'],
            }}
            rightSection={
              <Icons.NewLinkTab
                onClick={() =>
                  router.push(
                    `/leads/${
                      updateTaskForm.getInputProps('person.id').defaultValue
                    }`,
                  )
                }
                style={{ cursor: 'pointer' }}
              />
            }
            value={
              updateTaskForm.getInputProps('person.firstName').defaultValue +
              ' ' +
              updateTaskForm.getInputProps('person.lastName').defaultValue
            }
          />
          <ModalSelect
            label="Type of Task"
            disabled
            options={TASK_TYPE_OPTIONS}
            showArrowIcon={false}
            {...updateTaskForm.getInputProps('type')}
            key={updateTaskForm.key('type')}
            allowDeselect={false}
          />
          <ModalSelect
            label="Action"
            placeholder="Enter Action"
            {...updateTaskForm.getInputProps('action')}
            options={TASK_ACTION_OPTIONS}
            key={updateTaskForm.key('action')}
            allowDeselect={false}
            readOnly={!hasUpdateTaskPolicy}
            showArrowIcon={hasUpdateTaskPolicy}
          />
          <Tooltip
            position="bottom"
            display={action ? 'none' : 'block'}
            label={TOOLTIP_SUBJECT}
          >
            <div>
              <ModalSelect
                label="Subject"
                placeholder="Enter Subject"
                {...updateTaskForm.getInputProps('subject')}
                options={taskSubjectValues}
                key={updateTaskForm.key('subject')}
                value={subject}
                showArrowIcon={hasUpdateTaskPolicy && !!action}
                allowDeselect={false}
                readOnly={!hasUpdateTaskPolicy}
              />
            </div>
          </Tooltip>
          <ModalSelect
            label="Assign To"
            placeholder={
              updateTaskForm.getInputProps('assignedTo.firstName').defaultValue
                ? updateTaskForm.getInputProps('assignedTo.firstName')
                    .defaultValue +
                  ' ' +
                  updateTaskForm.getInputProps('assignedTo.lastName')
                    .defaultValue
                : 'Select Assignee'
            }
            {...updateTaskForm.getInputProps('assignedToId')}
            options={assignees ?? []}
            key={updateTaskForm.key('assignedTo')}
            allowDeselect={false}
            searchable
            nothingFoundMessage="No users found"
            showArrowIcon={hasUpdateTaskPolicy}
            readOnly={!hasUpdateTaskPolicy}
          />
          <DatePickerInput
            required
            valueFormat="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            label="Due Date"
            key={updateTaskForm.key('activityDate')}
            rightSection={
              <Icons.Calendar className={styles['calender-icon']} />
            }
            highlightToday={true}
            {...updateTaskForm.getInputProps('activityDate')}
            popoverProps={{
              offset: 0,
              width: 'target',
              withinPortal: false,
              transitionProps: { transition: 'scale-y', duration: 300 },
              middlewares: {
                flip: false,
                shift: false,
                inline: true,
              },
            }}
            classNames={{
              input: styles['datePicker-input'],
              label: styles['datePicker-label'],
            }}
            readOnly={!hasUpdateTaskPolicy}
          />
          <ModalSelect
            label="Priority"
            {...updateTaskForm.getInputProps('priority')}
            key={updateTaskForm.key('priority')}
            options={TASK_PRIORITY_OPTIONS}
            required={false}
            allowDeselect={false}
            readOnly={!hasUpdateTaskPolicy}
            showArrowIcon={hasUpdateTaskPolicy}
          />
          <ModalSelect
            label="Status"
            {...updateTaskForm.getInputProps('status')}
            key={updateTaskForm.key('status')}
            options={TASK_STATUS_OPTIONS}
            required={false}
            allowDeselect={false}
            readOnly={!hasUpdateTaskPolicy}
            showArrowIcon={hasUpdateTaskPolicy}
          />

          <Stack style={{ alignSelf: 'center' }}>
            {assignPerson && (
              <Checkbox
                label="Assign Record to Organizer"
                radius="3px"
                {...updateTaskForm.getInputProps('isPersonAssign')}
                key={updateTaskForm.key('isPersonAssign')}
                className="mt-7"
                disabled={!hasUpdateTaskPolicy}
              />
            )}
            <Checkbox
              label="Notify Assignee via Email"
              radius="3px"
              {...updateTaskForm.getInputProps('isNotify')}
              key={updateTaskForm.key('isNotify')}
              className="mt-7"
              disabled={!hasUpdateTaskPolicy}
            />
          </Stack>
          <TextInput
            label="Submitted by"
            readOnly
            classNames={{
              wrapper: styles['record-info-wrapper'],
              input: styles['record-info-input'],
              label: styles['record-info-label'],
            }}
            defaultValue={
              updateTaskForm.getInputProps('createdBy.username').defaultValue
            }
          />
          <DatePickerInput
            readOnly
            valueFormat="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            label="Submitted Date"
            key={updateTaskForm.key('createdAt')}
            rightSection={
              <Icons.Calendar className={styles['calender-icon']} />
            }
            highlightToday={true}
            {...updateTaskForm.getInputProps('createdAt')}
            classNames={{
              input: styles['datePicker-input'],
              label: styles['datePicker-label'],
            }}
          />
          <TextInput
            label="Last Edited By"
            readOnly
            value={updateTaskForm.getValues()['updatedBy']?.username ?? ''}
            classNames={{
              wrapper: styles['record-info-wrapper'],
              input: styles['record-info-input'],
              label: styles['record-info-label'],
            }}
          />
          <DatePickerInput
            valueFormat="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            label="Last Edited Date"
            readOnly
            key={updateTaskForm.key('updatedAt')}
            rightSection={
              <Icons.Calendar className={styles['calender-icon']} />
            }
            highlightToday={true}
            {...updateTaskForm.getInputProps('updatedAt')}
            classNames={{
              input: styles['datePicker-input'],
              label: styles['datePicker-label'],
            }}
          />
        </SimpleGrid>
        {selectedItem?.chat?.id && (
          <Paper mb={32} mt={32}>
            <TaskNotes chatId={selectedItem?.chat?.id} />
          </Paper>
        )}
        <Flex justify="space-between" mt="30px">
          <Button
            variant="default"
            className={styles['button-cancel']}
            disabled={updateTaskMutationStatus.isLoading}
            onClick={() => {
              modals.closeAll()
            }}
          >
            Cancel
          </Button>
          <Button
            rightSection={<Icons.ArrowRight />}
            className={styles['button-save']}
            type="submit"
            disabled={
              !hasUpdateTaskPolicy || updateTaskMutationStatus.isLoading
            }
            loading={updateTaskMutationStatus.isLoading}
          >
            Save
          </Button>
        </Flex>
      </form>
    </div>
  )
}

export default withErrorBoundaryAndPolicyCheck(
  UpdateTaskModal,
  WbfPolicies.is_get_tasks_enabled,
)
